import classnames from "classnames";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useEffect } from "react";

import {
  FormattedFieldError,
  isAriaInvalid,
  defaultRequiredMessage,
  emailPatternValidation,
} from "common/core/form/error";
import ContentDivider from "common/core/content_divider";
import { StyledTextInput } from "common/core/form/text";
import { useForm } from "common/core/form";
import { CURRENT_PORTAL } from "constants/app_subdomains";
import APP from "constants/applications";

import { Continue, SignInWithGoogle, SignUp, LoginHeader } from "./components";
import Styles from "./index.module.scss";

export type FormValues = {
  email: string;
  password: string;
};

type Props = {
  isOktaPluginLogin: boolean;
  onEmailSubmit: (values: FormValues) => void;
  onGoogleSignIn: (authCode: string) => void;
  authTypeError: boolean;
  ssoRequired: boolean;
};

const MESSAGES = defineMessages({
  emailLabel: {
    id: "d5621515-9f9b-483f-8a06-7034eefefc2a",
    defaultMessage: "Email address",
  },
  passwordLabel: {
    id: "95335d4e-dea2-45a8-8b49-69e357a03121",
    defaultMessage: "Password",
  },
});

const LABEL_MESSAGES = defineMessages({
  emailLabel: {
    id: "a3cee612-444a-4991-8a9b-e8f301f13bdb",
    defaultMessage: "Account email address",
  },
});

export default function EmailScreen({
  onEmailSubmit,
  onGoogleSignIn,
  isOktaPluginLogin,
  authTypeError,
  ssoRequired,
}: Props) {
  const intl = useIntl();
  const googleSignInEnabled =
    ![APP.NOTARY, APP.TITLE_AGENCY, APP.LENDER, APP.ADMIN].includes(CURRENT_PORTAL) && !ssoRequired;

  const { handleSubmit, register, formState, setFocus } = useForm<FormValues>({
    mode: "all",
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const { errors, isValid, isDirty } = formState;

  const invalid = errors.email && isDirty;

  useEffect(() => {
    setFocus("email");
  }, [setFocus]);

  return (
    <>
      <LoginHeader liveRegion="polite" />
      <form className={Styles.form} onSubmit={handleSubmit(onEmailSubmit)}>
        <StyledTextInput
          aria-invalid={isAriaInvalid(invalid)}
          className={errors.email ? Styles.marginBottom : Styles.input}
          autoComplete="username"
          label={intl.formatMessage(MESSAGES.emailLabel)}
          aria-label={intl.formatMessage(LABEL_MESSAGES.emailLabel)}
          data-automation-id="email-field"
          type="email"
          {...register("email", {
            required: defaultRequiredMessage(intl),
            pattern: emailPatternValidation(intl),
          })}
        />
        {invalid && (
          <div className={Styles.marginBottom}>
            <FormattedFieldError inputName={"email"} error={errors.email} />
          </div>
        )}
        {/* Hidden for Autofill/Password Managers, but not Okta Plugin: RGB-2216, SE-3293
            -- It is **required** that the following input has attribute type="password"
            -- for the Okta Plugin to be able to enter the user's password
        */}
        {isOktaPluginLogin && (
          <StyledTextInput
            autoComplete="current-password"
            aria-invalid={isAriaInvalid(errors.password)}
            className={Styles.input}
            placeholder="Password"
            type="password"
            {...register("password")}
          />
        )}
        <Continue disabled={!isValid} />
      </form>
      {authTypeError && (
        <p className={classnames(Styles.formError, Styles.error)}>
          <FormattedMessage
            id="f699ee4e-ee84-4ea3-9d9b-3d4c10600abf"
            defaultMessage="* Invalid email"
          />
        </p>
      )}

      <SignUp />
      {googleSignInEnabled && (
        <>
          <ContentDivider />
          <SignInWithGoogle onGoogleSignIn={onGoogleSignIn} />
        </>
      )}
    </>
  );
}
